/**
 * Respuestas: botones tocados (barras), bajas por vía, pedidos de alta y de ayuda (Advanced
 * Opt-Out), entrantes por anuncio, respuestas que citan el mensaje y ventanas de 24 h abiertas.
 * La variante compacta (detalle de campaña) deja sólo botones y bajas.
 *
 * Un START no saca a nadie de no contactar (P-3): acá se cuenta y se manda a revisarlo en Bajas.
 *
 * «Bajas por vía» cuenta lo mismo que el recuadro «Bajas» y el embudo: una por mensaje de campaña marcado como baja
 * (el backend las arma desde los mensajes). Antes salían de las respuestas y no cerraban con el recuadro, y quien
 * había frenado las promociones desde WhatsApp (Meta no se lo entrega: 131050) no tenía vía y se perdía.
 */

import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { BajasPorVia, BotonTocado, OptOutTipos } from "../../types.estadisticas";
import { COLORES } from "../../logic/estadisticas";
import { num, plural } from "../../../campana-nueva/utils/format";
import { ESTILO_TOOLTIP, Leyenda } from "./comun";

function Cifra({ label, valor, sub }: { label: string; valor: number | null | undefined; sub?: string }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
      <p className="text-lg font-semibold text-slate-900">{valor === null || valor === undefined ? "—" : num(valor)}</p>
      <p className="text-xs text-slate-600">{label}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function TarjetaRespuestas({ botones, bajasPorVia, optOut, entrantesPorAnuncio, ventanasAbiertas24h, respuestasCitando, compacta = false }: {
  botones: BotonTocado[] | null | undefined; bajasPorVia: BajasPorVia | null | undefined; optOut?: OptOutTipos | null;
  entrantesPorAnuncio?: number; ventanasAbiertas24h?: number; respuestasCitando?: number; compacta?: boolean;
}) {
  // Con las personas (una campaña o una plantilla), las barras son personas distintas: lo que pidió Alan el 26/10 («cuánta
  // gente tocó el botón»). Sin ellas (toda la marca), toques, como antes.
  const porPersona = (botones || []).some((b) => typeof b.personas === "number");
  const valorDe = (b: BotonTocado) => (porPersona ? Number(b.personas) || 0 : Number(b.cantidad) || 0);
  const bs = [...(botones || [])].sort((a, b) => valorDe(b) - valorDe(a)).slice(0, 10)
    .map((b) => ({ ...b, valor: valorDe(b), etiqueta: b.etiqueta || b.payload || "(sin texto)", fill: b.esBaja ? COLORES.bajas : COLORES.respuestas }));
  const nombreValor = porPersona ? "Personas" : "Toques";
  const hayBajaEnBotones = bs.some((b) => b.esBaja);

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Botones tocados</h4>
        {!bs.length ? (
          <p className="text-sm text-slate-400">Nadie tocó un botón en estas fechas.</p>
        ) : (
          <>
            {hayBajaEnBotones && <div className="mb-2"><Leyenda items={[{ color: COLORES.respuestas, label: "Respuesta" }, { color: COLORES.bajas, label: "Pide la baja" }]} /></div>}
            <div style={{ width: "100%", height: Math.max(100, bs.length * 28 + 16) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bs} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={COLORES.grilla} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="etiqueta" width={120} tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={false} />
                  <Tooltip {...ESTILO_TOOLTIP} formatter={(v) => [Number(v).toLocaleString("es-AR"), nombreValor]} />
                  <Bar dataKey="valor" name={nombreValor} radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false}>
                    {bs.map((b) => <Cell key={`${b.payload}|${b.etiqueta}`} fill={b.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {porPersona && (
              <ul className="mt-2 space-y-0.5 text-sm text-slate-600" data-testid="botones-por-persona">
                {bs.map((b) => (
                  <li key={`${b.payload}|${b.etiqueta}`}>
                    <strong className="text-slate-800">{b.etiqueta}</strong>: {num(b.valor)} {plural(b.valor, "persona", "personas")}
                    {b.cantidad !== b.valor ? ` (${num(b.cantidad)} ${plural(b.cantidad, "toque", "toques")})` : ""}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Bajas por vía</h4>
        <p className="text-[11px] text-slate-400 mb-2">Una por persona y campaña: suman lo mismo que «Bajas», que va sobre los enviados.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Cifra label="Con el botón" valor={bajasPorVia?.boton} />
          <Cifra label="Escribiendo la palabra" valor={bajasPorVia?.palabra} />
          <Cifra label="Baja automática" valor={bajasPorVia?.optOutTwilio} sub="Respondió STOP o BAJA" />
          {/* un backend anterior no la manda: «—», no un 0 inventado */}
          <Cifra label="Desde WhatsApp" valor={bajasPorVia ? bajasPorVia.desdeWhatsapp ?? null : undefined} sub="Ya había frenado las promociones de la marca: no le llegó" />
          {(Number(bajasPorVia?.sinDetalle) || 0) > 0 && <Cifra label="Sin detalle" valor={bajasPorVia?.sinDetalle} sub="Su respuesta se borró" />}
          <Cifra label="Total" valor={bajasPorVia?.total} />
        </div>
      </div>

      {!compacta && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Otras respuestas</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            <Cifra label="Pidieron volver (ALTA)" valor={optOut?.start} sub="Se revisan a mano en Bajas" />
            <Cifra label="Pidieron ayuda" valor={optOut?.help} />
            <Cifra label="Llegaron por un anuncio" valor={entrantesPorAnuncio} sub="Click-to-WhatsApp" />
            <Cifra label="Citaron el mensaje" valor={respuestasCitando} sub="Respuesta directa" />
            <Cifra label="Ventanas de 24 h abiertas" valor={ventanasAbiertas24h} sub="Escribieron en las últimas 24 h" />
          </div>
          {(Number(optOut?.start) || 0) > 0 && (
            <Link to="/activa/oficial/bajas" className="mt-2 inline-block text-xs font-semibold text-violet-700 hover:underline">Revisar los pedidos de alta</Link>
          )}
        </div>
      )}
      {compacta && typeof respuestasCitando === "number" && (
        <p className="text-xs text-slate-500">{num(respuestasCitando)} {respuestasCitando === 1 ? "respuesta citó" : "respuestas citaron"} el mensaje de la campaña.</p>
      )}
    </div>
  );
}

export default TarjetaRespuestas;
